import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileQuestion, AlertTriangle, HelpCircle, Clock, Check, X, UploadCloud, MessageCircle } from "lucide-react";
import { ReplyToSopDialog } from "@/components/ReplyToSopDialog";
import { reviewChanged } from "@/components/AppShell";
import { toast } from "sonner";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { StatusBadge, CoveragePill } from "@/components/Badges";
import { pick, TranslateToggle, useOriginal } from "@/components/Translate";
import { t, locale } from "@/i18n";
import { Tt } from "@/components/Tt";

const Stat = ({ icon: Icon, label, value, tone, testid }) => (
  <div className="card rise" data-testid={testid}>
    <Icon size={18} style={{ color: `var(--status-${tone})` }} />
    <p className="text-3xl font-extrabold mt-2">{value}</p>
    <p className="text-sm muted">{t(label)}</p>
  </div>
);

function QuestionItem({ q, api, reload, sops }) {
  const [reply, setReply] = useState("");
  const [askSave, setAskSave] = useState(false);
  const orig = useOriginal();
  const send = async (path, body) => {
    try { await api.post(`/questions/${q.id}/${path}`, body); toast.success(t("Done")); reload(); reviewChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const sendReply = async () => {
    try { await api.post(`/questions/${q.id}/reply`, { reply }); toast.success(t("Reply sent")); setAskSave(true); } catch (e) { toast.error(errMsg(e)); }
  };
  const closeSave = (open) => { setAskSave(open); if (!open) { reload(); reviewChanged(); } };
  return (
    <div className="py-4 border-b border-[var(--border-color)] last:border-0 stack-sm" data-testid={`review-question-${q.id}`}>
      <div className="flex flex-wrap items-center gap-2"><CoveragePill coverage={q.coverage} /><span className="text-xs faint" data-testid={`review-asked-by-${q.id}`}>{t("Asked by")} {q.asked_by_name || t("Staff")}{q.asked_by_title ? `, ${q.asked_by_title}` : ""} · {fmtDate(q.created_at)}</span>{q.asked_count > 0 && <span className="chip chip-muted" data-testid={`review-asked-count-${q.id}`}>{t("asked {n} times", { n: q.asked_count + 1 })}</span>}{q.sensitive && <span className="chip chip-err" data-testid={`review-sensitive-${q.id}`}>{t("Sensitive")}</span>}{q.assigned_name && <span className="text-xs faint" data-testid={`review-assigned-${q.id}`}>→ {q.assigned_name}</span>}{String(q.channel).startsWith("customer") && <span className="chip chip-ok" data-testid={`review-customer-badge-${q.id}`}><MessageCircle size={11} /> {t("Customer")}</span>}</div>
      <p className="font-semibold" data-testid={`review-question-text-${q.id}`}>“{pick(q, "question", orig.isOriginal(q.id))}”</p>
      <TranslateToggle q={q} original={orig.isOriginal(q.id)} onToggle={() => orig.toggle(q.id)} />
      {q.missing && <p className="text-sm muted" data-testid={`review-missing-${q.id}`}>{t("Missing")}: {pick(q, "missing", orig.isOriginal(q.id))}</p>}
      {q.flag_reason && String(q.channel).startsWith("customer") && <p className="text-xs muted" data-testid={`review-flag-reason-${q.id}`}><b>{t("Why held back")}:</b> {q.flag_reason}</p>}
      {q.draft_answer && <div className="banner banner-info text-sm" data-testid={`review-draft-${q.id}`}><div className="stack-sm"><span><b>{t("AI draft")}:</b> {q.draft_answer}</span><button className="btn btn-sm btn-ghost self-start" onClick={() => setReply(q.draft_answer)} data-testid={`review-use-draft-${q.id}`}>{t("Use it")}</button></div></div>}
      <div className="flex gap-2">
        <input className="field text-sm" value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t(String(q.channel).startsWith("customer") ? "Reply to the customer…" : "Reply to the apprentice…")} data-testid={`review-reply-input-${q.id}`} />
        <button className="btn btn-primary btn-sm whitespace-nowrap shrink-0" disabled={!reply.trim() || askSave} onClick={sendReply} data-testid={`review-reply-button-${q.id}`}><Check size={14} /> {t("Reply")}</button>
        <button className="btn btn-ghost btn-sm" onClick={() => send("dismiss")} data-testid={`review-dismiss-button-${q.id}`}><X size={14} /></button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <ReplyToSopDialog q={q} sops={sops} api={api} reply={reply} onDone={() => { reload(); reviewChanged(); }} />
        <ReplyToSopDialog q={q} sops={sops} api={api} reply={reply} onDone={() => { reload(); reviewChanged(); }} afterReply open={askSave} onOpenChange={closeSave} />
        <Link to="/capture" className="text-xs text-[var(--accent-primary)] inline-flex items-center gap-1" data-testid={`review-capture-link-${q.id}`}><UploadCloud size={12} /> {t("Or capture new media")}</Link>
      </div>
    </div>
  );
}

function FlagItem({ f, api, reload }) {
  const resolve = async () => {
    try { await api.post(`/flags/${f.id}/resolve`, { note: "" }); toast.success(t("Flag resolved")); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="py-4 border-b border-[var(--border-color)] last:border-0 stack-sm" data-testid={`review-flag-${f.id}`}>
      <span className={`chip ${f.type === "contradiction" ? "chip-warn" : "chip-warn"}`}>{t(f.type === "contradiction" ? "Possible contradiction" : "Outdated procedure")}</span>
      <p className="text-sm"><Link className="font-semibold underline" to={`/sops/${f.sop_id}`}><Tt text={f.sop_title} /></Link>{f.other_sop_id && <> {t("vs")} <Link className="underline" to={`/sops/${f.other_sop_id}`}><Tt text={f.other_sop_title} /></Link></>}</p>
      <p className="text-sm muted">{f.detail}</p>
      <button className="btn btn-ghost btn-sm" onClick={resolve} data-testid={`review-flag-resolve-${f.id}`}><Check size={14} /> {t("Mark resolved")}</button>
    </div>
  );
}

const Section = ({ title, count, children, testid }) => (
  <div className="card" data-testid={testid}>
    <p className="mono-label mb-1">{t(title)} ({count})</p>
    {count === 0 ? <p className="text-sm faint py-3">{t("All clear.")}</p> : children}
  </div>
);

export default function ReviewQueue() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sops, setSops] = useState([]);
  const load = useCallback(() => {
    api.get("/sops").then((r) => setSops(r.data)).catch(() => {});
    return api.get("/review").then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  }, [api]);
  useEffect(() => { load(); }, [load]);
  if (err && !data) return <p className="page" data-testid="review-error">{err}</p>;
  if (!data) return <p className="page faint">{t("Loading review queue…")}</p>;
  const ready = data.drafts.filter((d) => d.status === "draft" && !data.needs_clarification.find((n) => n.id === d.id));
  const outdated = data.flags.filter((f) => f.type === "outdated").length + data.stale.length;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 stack">
      <div className="flex flex-wrap items-end justify-between gap-4 rise">
        <div>
          <p className="mono-label">{new Date().toLocaleDateString(locale(), { weekday: "long", month: "long", day: "numeric" })}</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">{t("Review")}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="chip chip-err !text-sm !py-1" data-testid="review-stat-unanswered">{t("{n} need action", { n: data.questions.length + data.flags.length + data.needs_clarification.length })}</span>
          <span className="chip chip-info !text-sm !py-1" data-testid="review-stat-ready">{t("{n} ready", { n: ready.length })}</span>
          <span className="chip chip-ok !text-sm !py-1" data-testid="review-stat-approved">{t("{n} approved", { n: sops.filter((s) => s.status === "published").length })}</span>
          {outdated > 0 && <span className="chip chip-warn !text-sm !py-1" data-testid="review-stat-outdated">{t("{n} outdated", { n: outdated })}</span>}
        </div>
      </div>
      <div className="grid lg:grid-cols-12 gap-s items-start">
        <div className="lg:col-span-7 stack">
          <Section title="Unanswered questions" count={data.questions.length} testid="review-questions-section">
            {data.questions.map((q) => <QuestionItem key={q.id} q={q} api={api} reload={load} sops={sops} />)}
          </Section>
          <Section title="Contradiction & outdated flags" count={data.flags.length} testid="review-flags-section">
            {data.flags.map((f) => <FlagItem key={f.id} f={f} api={api} reload={load} />)}
          </Section>
        </div>
        <div className="lg:col-span-5 stack">
          <Section title="Confusing procedures" count={(data.confusing || []).length} testid="review-confusing-section">
            {(data.confusing || []).map((c) => (
              <Link key={c.sop_id} to={`/sops/${c.sop_id}`} className="block py-3 border-b border-[var(--border-color)] last:border-0 hover:text-[var(--accent-primary)]" data-testid={`review-confusing-item-${c.sop_id}`}>
                <div className="flex justify-between gap-2 items-center"><span className="font-semibold"><Tt text={c.title} /></span><span className="chip chip-warn"><HelpCircle size={11} /> {t("{n} unclear", { n: c.count })}</span></div>
                {c.questions.map((t, i) => <p key={i} className="text-xs faint mt-1 truncate">“{t}”</p>)}
              </Link>
            ))}
          </Section>
          <Section title="Low-confidence ingestion · needs clarification" count={data.needs_clarification.length} testid="review-clarify-section">
            {data.needs_clarification.map((d) => (
              <Link key={d.id} to={`/sops/${d.id}`} className="block py-3 border-b border-[var(--border-color)] last:border-0 hover:text-[var(--accent-primary)]" data-testid={`review-clarify-item-${d.id}`}>
                <div className="flex gap-2 items-center"><StatusBadge status={d.status} /><span className="font-semibold"><Tt text={d.title} /></span></div>
                <p className="text-xs faint mt-1">{t("Open questions: {n}", { n: d.open_clarifications })}{d.low_quality_sources.length ? ` · ${t("Low-quality sources: {n}", { n: d.low_quality_sources.length })}` : ""}{d.confidence === "low" ? ` · ${t("low confidence")}` : ""}</p>
              </Link>
            ))}
          </Section>
          <Section title="Drafts ready to publish" count={ready.length} testid="review-ready-section">
            {ready.map((d) => <Link key={d.id} to={`/sops/${d.id}`} className="block py-2 text-sm hover:text-[var(--accent-primary)]" data-testid={`review-ready-item-${d.id}`}><Tt text={d.title} /> <span className="faint text-xs">· {fmtDate(d.updated_at)}</span></Link>)}
          </Section>
          {data.stale.length > 0 && (
            <Section title="Stale procedures (6+ months)" count={data.stale.length} testid="review-stale-section">
              {data.stale.map((s) => <Link key={s.id} to={`/sops/${s.id}`} className="block py-2 text-sm"><Tt text={s.title} /> · {fmtDate(s.updated_at)}</Link>)}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
